import React, { useEffect, useState } from "react";
import { Box } from "@mui/system";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import secureLocalStorage from "react-secure-storage";
import { Divider, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { useDebriefingStyles } from "../../styles/debriefing.styles";
import DebriefingService from "../../../services/DebriefingService";
import SheAiChecklistService from "../../../services/sheai/SheAiChecklistService";
import { AiChecklistItem } from "../../../services/sheai/SheAiChecklistTypes";
import palette from "../../../common/styles/palette";
import { TimestampChip } from "./timestampLinks";
import { TypeTextFeedback } from "../EventAppraiserFeedback/ContentCard";

/**
 * Um Item do Checklist na Correção: o enunciado, o Veredito do Avaliador e —
 * ao lado — a dica da IA com as evidências que a sustentam.
 *
 * A hierarquia é deliberada e foi o motivo da recusa da primeira entrega: quem
 * decide é o professor, a IA só sugere. Por isso o `Select` é o elemento
 * grande, a dica é um chip, e a evidência é uma lista curta com o timestamp
 * clicável. Justificativa, critério a critério, confiança, nota sugerida e log
 * de execução ficam na aba Dados IA — aqui roubavam a tela do checklist.
 */

/** O `scale` que o backend persiste — mesma lista de `available_scales`. */
const VEREDITOS = ["done", "partially_done", "done_wrong", "not_done"] as const;

type Veredito = (typeof VEREDITOS)[number];

const ehVeredito = (valor: string): valor is Veredito =>
  (VEREDITOS as readonly string[]).includes(valor);

const DICA_IA: Record<Veredito, { rotulo: string; cor: string; fundo: string }> = {
  done: { rotulo: "Fez", cor: "#0a6b46", fundo: palette.emeraldSoft },
  partially_done: { rotulo: "Fez parcialmente", cor: "#8a4b00", fundo: palette.warningSoft },
  done_wrong: { rotulo: "Fez errado", cor: palette.dangerText, fundo: palette.dangerSoft },
  not_done: { rotulo: "Não fez", cor: palette.textMuted, fundo: palette.muted },
};

/** Mesma ordem e rótulos da Entrevista (RegrasDoItem) e do ChecklistIntegroSection. */
const FACETAS: [string, string][] = [
  ["speech", "O que ouvir"],
  ["video", "O que ver"],
  ["correct_when", "Correto quando"],
  ["not_done_when", "Não realizou quando"],
  ["note", "Não penalizar por"],
];

interface ChecklistItem {
  item_id: number | string;
  question?: string;
  scale_value?: string | null;
  /** As 5 facetas da Detecção (o que a IA foi instruída a observar) — F3. */
  detection?: Record<string, string> | null;
  /** Item crítico/eliminatório, declarado pelo professor na autoria. */
  critical?: number | boolean | null;
}

interface SkillItemProps {
  stationId: number | string;
  debriefingId: number | string;
  skillId: number | string;
  item: ChecklistItem;
  /** O que a IA disse sobre ESTE item; ausente quando não houve análise. */
  ia?: AiChecklistItem;
  isAppraiser?: boolean;
}

export default function SkillItem({
  stationId,
  debriefingId,
  skillId,
  item,
  ia,
  isAppraiser,
}: SkillItemProps) {
  const { t } = useTranslation();
  const debriefingStyle = useDebriefingStyles();

  const perfil = secureLocalStorage.getItem("role");
  const somenteLeitura = !isAppraiser;
  const isStudent = Number(perfil) === 5 && !isAppraiser;

  const [veredito, setVeredito] = useState<Veredito | "">("");
  const [scaleId, setScaleId] = useState("");
  // F3: a Detecção fica recolhida — contexto sob demanda, não ruído na decisão.
  const [verDeteccao, setVerDeteccao] = useState(false);

  useEffect(() => {
    if (!item?.item_id || !debriefingId) return;
    const gravado = item.scale_value || "";
    setVeredito(ehVeredito(gravado) ? gravado : "");
    DebriefingService.getDebriefingScale(debriefingId, item.item_id)
      .then((resposta) => setScaleId(resposta?.data?.id || ""))
      .catch(() => setScaleId(""));
  }, [item, debriefingId]);

  /**
   * Registra se o professor concordou com a IA. É telemetria do agente, não a
   * nota: melhor esforço, e uma falha aqui não pode derrubar a Correção que já
   * foi gravada em `debriefing/scales`.
   */
  const registrarConcordancia = (escolhido: Veredito) => {
    if (!ia?.result) return;
    const idProfessor = secureLocalStorage.getItem("id");
    const concordou = escolhido === ia.result;
    SheAiChecklistService.postChecklistFeedback({
      debriefing_id: debriefingId,
      item_id: item.item_id,
      action: concordou ? "agree" : "disagree",
      ...(concordou ? {} : { adjusted_result: escolhido }),
      professor_id:
        typeof idProfessor === "string" || typeof idProfessor === "number"
          ? idProfessor
          : null,
    }).catch(() => {});
  };

  const gravar = async (evento: SelectChangeEvent) => {
    const escolhido = evento.target.value;
    if (escolhido !== "" && !ehVeredito(escolhido)) return;
    setVeredito(escolhido);
    if (escolhido === "") return;

    try {
      if (scaleId) {
        const resposta = await DebriefingService.updateDebriefingScales(scaleId, {
          scale: escolhido,
        });
        toast(resposta?.data?.Msg, { type: "success" });
      } else {
        const resposta = await DebriefingService.storeDebriefingScales({
          debriefing_id: debriefingId,
          station_id: stationId,
          skill_id: skillId,
          item_id: item.item_id,
          scale: escolhido,
        });
        setScaleId(resposta?.data?.id || "");
        toast(resposta?.data?.Msg, { type: "success" });
      }
      registrarConcordancia(escolhido);
    } catch (e) {
      toast("Não foi possível gravar a avaliação deste item.", { type: "error" });
    }
  };

  const classeIa = ia?.result && ehVeredito(ia.result) ? ia.result : undefined;
  const dica = classeIa ? DICA_IA[classeIa] : undefined;
  const evidencias = ia?.evidence || [];
  const divergiu = !!classeIa && !!veredito && veredito !== classeIa;
  const facetasPreenchidas = FACETAS.filter(
    ([chave]) => (item.detection?.[chave] || "").trim() !== ""
  );
  // TINYINT(1) pode chegar como 1, "1" ou true conforme a serialização.
  const ehCritico = Number(item.critical) === 1;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Typography sx={{ fontWeight: 600 }}>{item?.question}</Typography>
        {ehCritico && (
          <Box
            title="Item crítico declarado na autoria: a falha aqui invalida o desempenho — quem reprova é o Avaliador, a IA apenas sinaliza."
            sx={{
              borderRadius: "4px",
              px: 0.75,
              py: 0.25,
              fontSize: "0.6875rem",
              fontWeight: 700,
              letterSpacing: "0.03em",
              color: palette.dangerText,
              backgroundColor: palette.dangerSoft,
              whiteSpace: "nowrap",
            }}
          >
            ELIMINATÓRIO
          </Box>
        )}
      </Box>

      {/* F3 (BHCBAI-24): a Detecção que o professor autora na Entrevista aparece
          na Correção — recolhida por padrão para não disputar a decisão, a um
          clique de distância. É o texto exato que o agente recebeu no prompt. */}
      {!isStudent && facetasPreenchidas.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            component="button"
            onClick={() => setVerDeteccao(!verDeteccao)}
            sx={{
              background: "none",
              border: "none",
              p: 0,
              cursor: "pointer",
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: palette.brandDark,
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            {verDeteccao
              ? "Ocultar o que a IA observa neste item"
              : "O que a IA observa neste item"}
          </Typography>
          {verDeteccao && (
            <Box
              sx={{
                mt: 1,
                px: 2,
                py: 1.5,
                borderRadius: "8px",
                border: `1px solid ${palette.lilacMid}55`,
                backgroundColor: palette.brandWash,
                display: "grid",
                gap: 1,
              }}
            >
              {facetasPreenchidas.map(([chave, rotulo]) => (
                <Box key={chave} sx={{ fontSize: "0.8125rem", lineHeight: 1.45 }}>
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      color: palette.brandDark,
                      mr: 0.75,
                    }}
                  >
                    {rotulo}:
                  </Typography>
                  <Typography component="span" sx={{ fontSize: "0.8125rem", color: palette.text }}>
                    {item.detection?.[chave]}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Veredito do professor e dica da IA lado a lado: a decisão é dele, a
          sugestão fica visível no instante em que ele decide. */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 220 }}>
          {isStudent ? (
            veredito ? (
              <TypeTextFeedback type={veredito} />
            ) : (
              <Typography variant="body2" sx={{ color: palette.textMuted }}>
                Ainda não avaliado
              </Typography>
            )
          ) : (
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={veredito}
              onChange={gravar}
              disabled={somenteLeitura}
              sx={{ backgroundColor: palette.background }}
            >
              <MenuItem value="">{t("select_option")}</MenuItem>
              {VEREDITOS.map((escala) => (
                <MenuItem key={escala} value={escala}>
                  {t(`scale_${escala}`)}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>

        {!isStudent && dica && (
          <Box
            title={
              divergiu
                ? "A IA sugeriu outra classificação; a sua prevalece."
                : "Sugestão da IA a partir do vídeo"
            }
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              whiteSpace: "nowrap",
              borderRadius: "999px",
              px: 1.25,
              py: 0.5,
              fontSize: "0.8125rem",
              fontWeight: 700,
              color: dica.cor,
              backgroundColor: dica.fundo,
              border: `1px solid ${divergiu ? palette.warning : "transparent"}`,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 15 }} />
            SheAI: {dica.rotulo}
          </Box>
        )}
      </Box>

      {!isStudent && evidencias.length > 0 && (
        <Box
          sx={{
            mt: 2,
            px: 2,
            py: 1.5,
            borderRadius: "8px",
            border: `1px solid ${palette.lilacMid}55`,
            backgroundColor: palette.brandWash,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: palette.brandDark,
              mb: 1,
            }}
          >
            Evidências
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, display: "grid", gap: 1 }}>
            {evidencias.map((ev, idx) => (
              <Box
                component="li"
                key={`${ev.ts_start}-${idx}`}
                sx={{ fontSize: "0.875rem", lineHeight: 1.45, color: palette.text }}
              >
                <TimestampChip
                  label={
                    ev.ts_end && ev.ts_end !== ev.ts_start
                      ? `${ev.ts_start}-${ev.ts_end}`
                      : ev.ts_start
                  }
                  ts={ev.ts_start}
                  sourceId={ev.source_id}
                />{" "}
                {ev.description}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Divider className={debriefingStyle.divider} />
    </Box>
  );
}
